/** Edit an angle's label / question / search terms (chip menu). Stays open until saved. */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReasonLine } from "@/components/ui/disabled-reason";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { FacetSummary } from "@/types/concept";

export type AngleFields = Partial<Pick<FacetSummary, "label" | "question" | "search_terms">>;

interface Props {
  open: boolean;
  facet: FacetSummary;
  saving: boolean;
  onSave: (changed: AngleFields) => void;
  onCancel: () => void;
  /** Pending edits to restore on open (e.g. after cancelling the project-question review). */
  initial?: AngleFields;
}

export function EditAngleDialog({ open, facet, saving, onSave, onCancel, initial }: Props) {
  const [label, setLabel] = useState(initial?.label ?? facet.label);
  const [question, setQuestion] = useState(initial?.question ?? facet.question);
  const [terms, setTerms] = useState(initial?.search_terms ?? facet.search_terms);

  useEffect(() => {
    if (open) {
      setLabel(initial?.label ?? facet.label);
      setQuestion(initial?.question ?? facet.question);
      setTerms(initial?.search_terms ?? facet.search_terms);
    }
    // Reset only when (re)opened for a facet — not on every cache refetch mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facet.id]);

  const changed: AngleFields = {};
  if (label.trim() !== facet.label) changed.label = label.trim();
  if (question.trim() !== facet.question) changed.question = question.trim();
  if (terms.trim() !== facet.search_terms) changed.search_terms = terms.trim();
  const why = !label.trim() || !question.trim() ? "Give the angle a label and a question."
    : Object.keys(changed).length === 0 ? "No changes yet." : null;
  const valid = why === null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit angle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="angle-label">Label</Label>
            <Input id="angle-label" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="angle-question">Question</Label>
            <Textarea id="angle-question" rows={4} maxLength={2000} value={question} onChange={(e) => setQuestion(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="angle-terms">Search terms</Label>
            <Input id="angle-terms" maxLength={1000} value={terms} onChange={(e) => setTerms(e.target.value)} />
          </div>
          <DialogDescription className="text-xs">Changing the question or search terms rescores this angle in the background.</DialogDescription>
        </div>
        <DialogFooter className="items-center">
          {why && <ReasonLine id="edit-angle-save-why" className="mr-auto">{why}</ReasonLine>}
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(changed)} disabled={!valid || saving}
            aria-describedby={why ? "edit-angle-save-why" : undefined}>
            {saving && <Loader2 className="size-3.5 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
